import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  TextField,
  Tab,
  Tabs,
  Typography,
  Card,
  Divider,
  IconButton,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import SearchBar from "../../components/SearchBar";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useLazyGetProductVariantsQuery,
  useUpdateVariantMutation,
} from "../../service/product";
import { showError, showToast } from "../../constants/toasts";
import { ProductVariant } from "../../types/General";
import dayjs from "dayjs";
import CustomPagination from "../../components/CustomPagination";
import Loader from "../../constants/Loader";
const ManageInventory = () => {
  const [getProductVariants] = useLazyGetProductVariantsQuery();
  const [updateVariantAPI] = useUpdateVariantMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [variantData, setVariantData] = useState<ProductVariant[]>([]);
  const [count, setCount] = useState(10);
  const [page, setPage] = useState(1);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const demandForecastData = [
    { day: "Mon", sales: 40 },
    { day: "Tue", sales: 52 },
    { day: "Wed", sales: 48 },
    { day: "Thu", sales: 70 },
    { day: "Fri", sales: 90 },
    { day: "Sat", sales: 110 },
    { day: "Sun", sales: 65 },
  ];

  const handleVariantChange = (
    index: number,
    field: "quantity" | "maxPurchaseLimit" | "thresholdLimit",
    value: string
  ) => {
    setVariantData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value === "" ? "" : Number(value),
      };
      return updated;
    });
  };

  const handleUpdate = async (item: ProductVariant) => {
    const body = {
      quantity: item.quantity,
      maxPurchaseLimit: item.maxPurchaseLimit,
      thresholdLimit: item?.thresholdLimit,
    };

    try {
      const response = await updateVariantAPI({ body, id: item?.id }).unwrap();
      showToast(response?.message || "");
      getProductVariantsApi();
    } catch (err: any) {
      showError(err?.data?.message || "Update failed");
    }
  };

  const getProductVariantsApi = async () => {
    setLoading(true);
    try {
      const response = await getProductVariants({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVariantData(response?.data?.list || []);
        setCount(response?.data?.total);
      } else {
        setVariantData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getProductVariantsApi();
  }, []);

  return (
    <>
      <Loader isLoad={loading} />

      <div className="main_title">
        <h1>Inventory Management</h1>
      </div>

      {/* -------------------- STAT CARDS -------------------- */}
      <div className="stats_boxes gap_m">
        <a href="/manage-products" className="cards">
          <h2>42 Active Products</h2>
          <p>Total Products</p>
          <figure className="sucess">
            <img src="/static/images/bag.svg" alt="Total Products" />
          </figure>
          <div className="rating_box">
            <p>
              <span>
                <ArrowUpwardIcon />
                +8
              </span>
              this month
            </p>
          </div>
        </a>

        <div className="cards">
          <figure className="blue">
            <img src="/static/images/star.svg" alt="Low Stock" />
          </figure>
          <h2>7 Low Stock</h2>
          <p>Low Stock Alerts</p>
          <div className="rating_box">
            <p>
              <span>
                <ArrowUpwardIcon />
                +2
              </span>
              this month
            </p>
          </div>
        </div>

        <div className="cards">
          <figure className="sucess">
            <img src="/static/images/reward_icon.svg" alt="Inventory Value" />
          </figure>
          <h2>AED 12,450</h2>
          <p>Inventory Value</p>
          <div className="rating_box">
            <p>
              <span>
                <ArrowUpwardIcon />
                +5%
              </span>
              this month
            </p>
          </div>
        </div>

        {/* ✅ New Card: In Stock */}
        <div className="cards">
          <figure className="sucess">
            <img src="/static/images/bag.svg" alt="In Stock" />
          </figure>
          <h2>37 In Stock</h2>
          <p>Total Products Currently Available</p>
          <div className="rating_box">
            <p>
              <span>
                <ArrowUpwardIcon />
                +3
              </span>
              this month
            </p>
          </div>
        </div>

        {/* ✅ New Card: Out of Stock */}
        <div className="cards">
          <figure className="blue">
            <img src="/static/images/bag.svg" alt="Out of Stock" />
          </figure>
          <h2>5 Out of Stock</h2>
          <p>Products With Zero Quantity</p>
          <div className="rating_box">
            <p>
              <span>
                <ArrowUpwardIcon />
                +1
              </span>
              this month
            </p>
          </div>
        </div>
      </div>

      {/* ----------------------- TABS ----------------------- */}
      <div className="tabs_container mt_20">
        <Box className="custom_tabs1">
          <Tabs
            value={tab}
            onChange={handleTabChange}
            textColor="primary"
            className="custom_tabs_links"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            <Tab label="Inventory" />
            <Tab label="AI Insights" />
            <Tab label="Stock History" />
          </Tabs>
        </Box>
      </div>

      {/* -------------------- TAB 0 : INVENTORY -------------------- */}
      {tab === 0 && (
        <div className="cards ">
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search..."
              />
            </div>
            <div className="right_s">
              <Button className="btn btn_primary">Export CSV</Button>
            </div>
          </div>

          <TableContainer className="table_container" sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Product Details</TableCell>
                  <TableCell>Variant Name</TableCell>

                  <TableCell>Selling Price</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Purchase Limit</TableCell>
                  <TableCell>Out Of Stock</TableCell>

                  <TableCell>Category</TableCell>
                  <TableCell>Sub-category</TableCell>
                  <TableCell>Stock Threshold</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>AI Recommendation</TableCell>

                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {variantData?.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{index + 1 || "-"}</TableCell>
                    <TableCell>{item?.product?.prodId || "-"}</TableCell>

                    <TableCell>
                      <Box className="user_block">
                        <figure>
                          <img
                            src={item?.media?.[0]?.url}
                            alt={item?.product?.name}
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "4px",
                              objectFit: "cover",
                              background: "#f5f5f5",
                            }}
                          />
                        </figure>
                        <p>
                          <b>{item?.product?.name}</b>
                        </p>
                      </Box>
                    </TableCell>

                    <TableCell>{item?.variantName || "-"}</TableCell>
                    <TableCell>
                      <b>AED {item?.price || "0"}</b>
                    </TableCell>

                    <TableCell className="form">
                      <TextField
                        size="small"
                        type="text"
                        value={item.quantity ?? "0"}
                        onChange={(e) =>
                          handleVariantChange(index, "quantity", e.target.value)
                        }
                      />
                    </TableCell>

                    <TableCell className="form">
                      <TextField
                        size="small"
                        type="text"
                        value={item.maxPurchaseLimit ?? "0"}
                        onChange={(e) =>
                          handleVariantChange(
                            index,
                            "maxPurchaseLimit",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>

                    <TableCell>
                      <SwitchToggle
                        disabled
                        defaultChecked={item.quantity > 0}
                      />
                    </TableCell>

                    <TableCell>{item?.product?.category?.name}</TableCell>
                    <TableCell>{item?.product?.subCategory?.name}</TableCell>
                    <TableCell className="form">
                      <TextField
                        size="small"
                        type="text"
                        value={item?.thresholdLimit ?? "0"}
                        onChange={(e) =>
                          handleVariantChange(
                            index,
                            "thresholdLimit",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>AED {item?.discountedPrice || "0"}</TableCell>
                    <TableCell>
                      {item?.updatedAt
                        ? dayjs(item?.updatedAt).format("DD MMMM YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>{"-"}</TableCell>

                    <TableCell>
                      <div className="tbl_action">
                        <Button
                          variant="contained"
                          className="btn_primary"
                          size="small"
                          onClick={() => handleUpdate(item)}
                        >
                          Update
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {variantData?.length ? (
            <CustomPagination
              currentPage={page - 1}
              totalPages={Math.ceil(count / 10)}
              count={count}
              pageSize={10}
              onPageChange={(page) => setPage(page + 1)}
            />
          ) : null}
        </div>
      )}

      {/* -------------------- TAB 1 : AI RESTOCK -------------------- */}
      {tab === 1 && (
        <div className="cards">
          {/* ------------------ CARD 1: AI Restock Suggestion ------------------ */}
          <Card
            sx={{
              borderLeft: "4px solid #D3B88C",
              padding: 2,
              boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ShoppingCartOutlinedIcon sx={{ color: "#D3B88C" }} />
              <Typography variant="h6" fontWeight={600}>
                AI Restock Suggestion
              </Typography>
            </Box>

            {/* --- Restock List --- */}
            <Box sx={{ mt: 2 }}>
              {[
                { item: "Traditional Abaya", days: 5, qty: 20 },
                { item: "Handcrafted Jewelry", days: 3, qty: 15 },
                { item: "Leather Sandals", days: 7, qty: 30 },
                { item: "Premium Scarf", days: 4, qty: 10 },
              ].map((s, i) => (
                <Typography key={i} sx={{ color: "#444", mb: 1 }}>
                  <b>
                    {i + 1}. {s.item}
                  </b>{" "}
                  — Restock in <b>{s.days} days</b>, Suggest:{" "}
                  <b>{s.qty} units</b>
                </Typography>
              ))}
            </Box>

            <Box sx={{ mt: 1 }}>
              <IconButton sx={{ color: "#D3B88C" }}>
                <ThumbUpAltOutlinedIcon />
              </IconButton>
              <IconButton sx={{ color: "#D3B88C" }}>
                <ThumbDownAltOutlinedIcon />
              </IconButton>
            </Box>
          </Card>

          {/* ------------------ CARD 2: AI Demand Forecast ------------------ */}
          <Card
            sx={{
              borderLeft: "4px solid #D3B88C",
              padding: 2,
              marginTop: "20px",
              boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUpOutlinedIcon sx={{ color: "#D3B88C" }} />
              <Typography variant="h6" fontWeight={600}>
                AI Demand Forecast
              </Typography>
            </Box>
            <Box sx={{ height: 220, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={demandForecastData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    stroke="#D3B88C"
                    strokeWidth={3}
                    dot={{ r: 4, stroke: "#8A6E45", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
            {/* Forecast data list */}
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: "#444", mb: 1 }}>
                📈 Expected weekly growth: <b>12–15%</b>
              </Typography>
              <Typography sx={{ color: "#444", mb: 1 }}>
                🔥 Peak demand days: <b>Thu, Fri, Sat</b>
              </Typography>
              <Typography sx={{ color: "#444", mb: 1 }}>
                🛍️ Top category this week: <b>Abayas & Accessories</b>
              </Typography>
              <Typography sx={{ color: "#444", mb: 1 }}>
                🎯 Suggested Strategy: Increase inventory buffer by <b>8%</b>.
              </Typography>
            </Box>
          </Card>

          {/* ------------------ CARD 3: FAST MOVERS VS CART ITEMS ------------------ */}
          <Card
            sx={{
              padding: 2,
              marginTop: "20px",
              border: "1px solid #D3B88C",
              boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
            }}
          >
            <Box display="flex" justifyContent="space-between">
              {/* Left list */}
              <Box width="50%">
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#D3B88C", mb: 1 }}
                >
                  Top 5 Fast Movers
                </Typography>

                {[
                  "Traditional Abaya",
                  "Designer Handbag",
                  "Handcrafted Jewelry",
                  "Silk Scarf Collection",
                  "Floral Footwear",
                ].map((item, i) => (
                  <Typography key={i} sx={{ color: "#444" }}>
                    {i + 1}. {item}
                  </Typography>
                ))}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

              {/* Right list */}
              <Box width="50%">
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#D3B88C", mb: 1 }}
                >
                  Items Left in Carts
                </Typography>

                <Typography sx={{ color: "#444" }}>
                  1. Embroidered Wallet — <b>12</b>
                </Typography>
                <Typography sx={{ color: "#444" }}>
                  2. Leather Backpack — <b>9</b>
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* ------------------ CARD 4: Feedback Summary ------------------ */}
          <Card
            sx={{
              padding: 2,
              marginTop: "20px",
              borderLeft: "4px solid #D3B88C",
              boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatBubbleOutlineOutlinedIcon sx={{ color: "#D3B88C" }} />
              <Typography variant="h6" fontWeight={600}>
                Customer Feedback Summary
              </Typography>
            </Box>

            <Typography sx={{ mt: 1, fontStyle: "italic", color: "#555" }}>
              “Frequent mention: high packaging quality.”
            </Typography>
          </Card>
        </div>
      )}
      {tab === 2 && (
        <div className="cards mt_20">
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search..."
              />
            </div>
            <div className="right_s">
              <Button className="btn btn_primary">Export CSV</Button>
            </div>
          </div>

          <TableContainer className="table_container" sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Upload Date & Time</TableCell>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Quantity Added</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sub-category</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* ---------- SAMPLE DUMMY LOGS ---------- */}
                {[
                  {
                    date: "12 Feb 2025 - 10:23 AM",
                    id: "#PROD_001",
                    name: "Wireless Mouse",
                    image: "/static/images/placeholder.png",
                    qty: "+20",
                    price: "$99.99",
                    category: "Electronics",
                    sub: "Accessories",
                  },
                  {
                    date: "11 Feb 2025 - 4:45 PM",
                    id: "#PROD_003",
                    name: "Bluetooth Speaker",
                    image: "/static/images/placeholder.png",
                    qty: "+50",
                    price: "$79.99",
                    category: "Electronics",
                    sub: "Audio Devices",
                  },
                  {
                    date: "10 Feb 2025 - 2:18 PM",
                    id: "#PROD_002",
                    name: "Gaming Keyboard",
                    image: "/static/images/placeholder.png",
                    qty: "+15",
                    price: "$149.99",
                    category: "Electronics",
                    sub: "Accessories",
                  },
                ].map((log, i) => (
                  <TableRow key={i}>
                    <TableCell>{log.date}</TableCell>

                    <TableCell>{log.id}</TableCell>

                    <TableCell>{log.name}</TableCell>

                    <TableCell>
                      <img
                        src={log.image}
                        alt={log.name}
                        style={{
                          width: "35px",
                          height: "35px",
                          borderRadius: "4px",
                          objectFit: "cover",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <span
                        style={{
                          color: log.qty.includes("+") ? "green" : "red",
                          fontWeight: 600,
                        }}
                      >
                        {log.qty}
                      </span>
                    </TableCell>

                    <TableCell>{log.price}</TableCell>

                    <TableCell>{log.category}</TableCell>

                    <TableCell>{log.sub}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </>
  );
};

export default ManageInventory;
